export type ParticipationStep = "tickets" | "guests" | "details" | "phone" | "profile" | "questions" | "email" | "payment" | "status" | "select" | "overview" | "name" | "done" | "cancelled" | "declined" | "expired" | "unavailable";
export type JourneyKind = "purchase" | "approval" | "claim" | "rsvp_handoff";
export type NavigationReason = "entry" | "next" | "back" | "history" | "recovery";
export type ParticipationOperation = "quote" | "send_code" | "verify_code" | "save_details" | "upload_photo" | "save_answers" | "prepare_payment" | "confirm_payment" | "claim" | "check_outcome";
export type ActionResult = "success" | "failure" | "unknown";
export type ParticipationOutcome = "request_sent" | "booking_confirmed" | "recipient_details_completed" | "app_handoff";

export type FlowStep = { id: ParticipationStep; complete: boolean; skip?: boolean; skipBack?: boolean };
export type FlowPolicy = { steps: FlowStep[]; terminal?: ParticipationStep; recovery?: ParticipationStep; fallback?: ParticipationStep; allowStatus?: boolean };

// URL navigation is the source of truth. Policy only corrects unmet prerequisites;
// completing a prerequisite must not push an explicitly revisited step forward.
export function resolveParticipationStep(requested: string | null, policy: FlowPolicy): ParticipationStep | null {
  if (!requested) return null;
  if (policy.terminal) return policy.terminal;
  if (policy.recovery) return policy.recovery;
  if (requested === "status" && policy.allowStatus) return "status";
  const index = policy.steps.findIndex(step => step.id === requested);
  if (index < 0) return policy.fallback ?? policy.steps.find(step => !step.complete)?.id ?? policy.steps.at(-1)?.id ?? null;
  const missing = policy.steps.slice(0, index).find(step => !step.complete);
  return missing?.id ?? policy.steps[index].id;
}

export function adjacentParticipationStep(step: ParticipationStep, policy: FlowPolicy, direction: -1 | 1): ParticipationStep | null {
  for (let index = policy.steps.findIndex(value => value.id === step) + direction; index >= 0 && index < policy.steps.length; index += direction) {
    if (!(direction === 1 ? policy.steps[index].skip : policy.steps[index].skipBack)) return policy.steps[index].id;
  }
  return null;
}

export type ParticipationState = {
  scope: string;
  pending: { id: string; operation: ParticipationOperation; blocksNavigation: boolean } | null;
  navigationReason: NavigationReason;
};
export type ParticipationEvent =
  | { type: "scope"; scope: string }
  | { type: "navigate"; reason: NavigationReason }
  | { type: "started"; pending: NonNullable<ParticipationState["pending"]> }
  | { type: "finished"; id: string };

export function participationReducer(state: ParticipationState, event: ParticipationEvent): ParticipationState {
  switch (event.type) {
    case "scope": return event.scope === state.scope ? state : { scope: event.scope, pending: null, navigationReason: "entry" };
    case "navigate": return state.pending?.blocksNavigation ? state : { ...state, navigationReason: event.reason };
    case "started": return state.pending ? state : { ...state, pending: event.pending };
    case "finished": return state.pending?.id === event.id ? { ...state, pending: null } : state;
  }
}

export function participationSearch(search: URLSearchParams, step: ParticipationStep | null): URLSearchParams {
  const next = new URLSearchParams(search);
  if (step) next.set("step", step);
  else next.delete("step");
  return next;
}
